import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { Link } from 'react-router-dom';

import {
    Box,
    Stack,
    StarRating,
    Button,
    Heading,
    InlineBulletList,
    ImageUnavailable,
    useId,
} from 'sarsaparilla';

function getPriceString({ priceNum, minPriceNum, maxPriceNum }) {
    const hasSimplePrice = !isNil(priceNum);
    const hasFullPriceRange = !isNil(minPriceNum) && !isNil(maxPriceNum);

    if (hasFullPriceRange && minPriceNum >= 0) {
        if (minPriceNum === maxPriceNum || minPriceNum > maxPriceNum) {
            return `$${Math.floor(minPriceNum)}`;
        }

        return `$${Math.floor(minPriceNum)} - $${Math.floor(maxPriceNum)}`;
    } else if (hasSimplePrice) {
        return `$${Math.floor(priceNum)}`;
    }

    return null;
}

export default function SearchResultCard({
    id: userId,
    linkUrl,
    imageUrl,
    heading,
    headingLevel = 2,
    metaItems = [],
    featureItems = [],
    children,
    rating,
    reviewCount,
    priceNum,
    minPriceNum,
    maxPriceNum,
    pricePerUnit,
    availability,
    className,
    actionButtonText = 'Book Now',
    isLinkToSameService = true,
    ...rest
}) {
    const priceString = getPriceString({ priceNum, minPriceNum, maxPriceNum });
    const id = useId(userId);

    return (
        <div data-component="SearchResultCard" className={className} {...rest}>
            {isLinkToSameService && (
                <Link id={id} to={linkUrl} aria-label={heading}>
                    &nbsp;
                </Link>
            )}
            {!isLinkToSameService && (
                <a id={id} href={linkUrl} aria-label={heading}>
                    &nbsp;
                </a>
            )}

            <div className="with-sidebar">
                <div className="sidebar">
                    {isNil(imageUrl) && <ImageUnavailable isStretchy />}
                    {!isNil(imageUrl) && (
                        <div
                            role="img"
                            aria-label={heading}
                            className="image"
                            style={{
                                backgroundImage: `url("${imageUrl}")`,
                            }}
                        />
                    )}
                </div>
                <div className="not-sidebar">
                    <div className="details">
                        <Box padding={['sm', 'md']}>
                            <Stack space="sm">
                                <div>
                                    <Heading headingLevel={headingLevel} appearance="h5">
                                        {heading}
                                    </Heading>
                                    {!!availability && (
                                        <Box
                                            shadow={1}
                                            className="availability"
                                            border={
                                                availability.toUpperCase() ===
                                                'UNAVAILABLE'
                                                    ? 'red'
                                                    : 'gray'
                                            }
                                            background={
                                                availability.toUpperCase() ===
                                                'UNAVAILABLE'
                                                    ? 'light-red'
                                                    : 'white'
                                            }
                                        >
                                            {availability}
                                        </Box>
                                    )}
                                    {!isEmpty(metaItems) && (
                                        <InlineBulletList className="meta-list mt-half">
                                            {metaItems.map((item, index) => (
                                                <li key={index}>{item}</li>
                                            ))}
                                        </InlineBulletList>
                                    )}
                                </div>
                                <div>
                                    {!isEmpty(featureItems) && (
                                        <InlineBulletList className="features-list">
                                            {featureItems.map((item, index) => (
                                                <li key={index}>{item}</li>
                                            ))}
                                        </InlineBulletList>
                                    )}

                                    {children}
                                </div>
                            </Stack>
                        </Box>
                        <Box
                            className="bottom-bar"
                            background="gray-1"
                            paddingY="xs"
                            paddingX={['sm', 'md']}
                        >
                            <StarRating rating={rating} reviewCount={reviewCount} />

                            {!!priceString && (
                                <div className="price">
                                    {priceString}
                                    {!!pricePerUnit && (
                                        <span className="price-per-unit">
                                            {`/ ${pricePerUnit}`}
                                        </span>
                                    )}
                                </div>
                            )}

                            {!priceString && (
                                <Button
                                    to={isLinkToSameService ? linkUrl : null}
                                    href={isLinkToSameService ? null : linkUrl}
                                    aria-label={`${actionButtonText}: ${heading}`}
                                    size="xs"
                                >
                                    {actionButtonText}
                                </Button>
                            )}
                        </Box>
                    </div>
                </div>
            </div>
        </div>
    );
}

SearchResultCard.propTypes = {
    id: PropTypes.string,
    linkUrl: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
    imageUrl: PropTypes.string,
    headingLevel: PropTypes.number,
    metaItems: PropTypes.array,
    featureItems: PropTypes.array,
    bonusItemsTitle: PropTypes.node,
    bonusItems: PropTypes.array,
    rating: PropTypes.number,
    reviewCount: PropTypes.number,
    priceNum: PropTypes.number,
    minPriceNum: PropTypes.number,
    maxPriceNum: PropTypes.number,
    pricePerUnit: PropTypes.string,
    availability: PropTypes.string,
    className: PropTypes.string,
    actionButtonText: PropTypes.string,
    children: PropTypes.node,

    /** If true, renders react router Links to preserve redux state */
    isLinkToSameService: PropTypes.bool,
};
